import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Project, ProjectStatus } from "@models/textToSpeech/project.model";
import {
  EmotionProjectReply,
  ProjectService,
} from "~/app/textToSpeech/project.service";

@Component({
  selector: "app-emotion-replies",
  templateUrl: "./emotion-replies.component.html",
  styleUrls: ["./emotion-replies.component.scss"],
})
export class EmotionRepliesComponent implements OnInit {
  public projectId: number;
  public spinner = false;
  public loadFailed = false;
  public project: Project | null = null;
  public dialogues: EmotionProjectReply[] = [];
  public savingDialogueIds = new Set<number>();
  public saveFailedDialogueIds = new Set<number>();
  public savedDialogueIds = new Set<number>();
  public audioGenerationRunning = false;
  public audioGenerationError = false;
  public audioGenerationQueued = false;
  public assembleMixRunning = false;
  public assembleMixError = false;
  public assembleMixSuccess = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly projectService: ProjectService
  ) {}

  ngOnInit(): void {
    this.projectId = Number(this.route.snapshot.paramMap.get("id"));
    if (!this.projectId) {
      this.router.navigate(["/projects"]);
      return;
    }

    this.loadProjectContext();
    this.loadDialogues();
  }

  public backToProjects(): void {
    this.router.navigate(["/projects"]);
  }

  public onSaveDialogue(
    line: EmotionProjectReply,
    translatedDialogue: string
  ): void {
    if (!line?.id || this.savingDialogueIds.has(line.id)) {
      return;
    }

    if (
      `${line.translatedDialogue ?? line.dialogue ?? ""}` ===
      `${translatedDialogue ?? ""}`
    ) {
      return;
    }

    this.savingDialogueIds.add(line.id);
    this.saveFailedDialogueIds.delete(line.id);
    this.savedDialogueIds.delete(line.id);

    this.projectService
      .updateEmotionReplyDialogue(this.projectId, line.id, translatedDialogue)
      .subscribe({
        next: () => {
          line.translatedDialogue = translatedDialogue;
          this.savingDialogueIds.delete(line.id);
          this.savedDialogueIds.add(line.id);
        },
        error: () => {
          this.savingDialogueIds.delete(line.id);
          this.saveFailedDialogueIds.add(line.id);
        },
      });
  }

  public get canGenerateEmotionAudio(): boolean {
    if (!this.project || this.audioGenerationRunning) {
      return false;
    }
    return this.project.status !== ProjectStatus.EmotionProcessing;
  }

  public get canAssembleAndMix(): boolean {
    if (!this.project || this.assembleMixRunning) {
      return false;
    }
    const blockedStatuses: string[] = [
      ProjectStatus.EmotionProcessing,
      ProjectStatus.Mixing,
    ];
    if (blockedStatuses.includes(this.project.status as string)) {
      return false;
    }
    return this.dialogues.some((d) => !!d.audioFilePath);
  }

  public generateEmotionAudio(): void {
    if (!this.project || this.audioGenerationRunning) {
      return;
    }

    this.audioGenerationRunning = true;
    this.audioGenerationError = false;
    this.audioGenerationQueued = false;

    this.projectService.generateEmotionAudio(this.projectId).subscribe({
      next: (response) => {
        this.audioGenerationRunning = false;
        this.audioGenerationQueued = response?.isSuccess === true;
        this.audioGenerationError = !this.audioGenerationQueued;
        if (this.audioGenerationQueued && this.project) {
          this.project = {
            ...this.project,
            status: ProjectStatus.EmotionProcessing,
          };
        }
      },
      error: () => {
        this.audioGenerationRunning = false;
        this.audioGenerationError = true;
        this.audioGenerationQueued = false;
      },
    });
  }

  public assembleAndMix(): void {
    if (!this.project || this.assembleMixRunning) {
      return;
    }

    this.assembleMixRunning = true;
    this.assembleMixError = false;
    this.assembleMixSuccess = false;

    this.projectService.assembleAndMixEmotionReplies(this.projectId).subscribe({
      next: (response) => {
        this.assembleMixRunning = false;
        this.assembleMixSuccess = response?.isSuccess === true;
        this.assembleMixError = !this.assembleMixSuccess;
        if (this.assembleMixSuccess && this.project) {
          this.project = {
            ...this.project,
            status: ProjectStatus.Mixing,
          };
        }
      },
      error: () => {
        this.assembleMixRunning = false;
        this.assembleMixError = true;
      },
    });
  }

  private loadProjectContext(): void {
    this.projectService.getById(this.projectId).subscribe({
      next: (project) => {
        this.project = project;
      },
      error: () => {
        this.project = null;
      },
    });
  }

  private loadDialogues(): void {
    this.spinner = true;
    this.loadFailed = false;

    this.projectService.initEmotionReplies(this.projectId).subscribe({
      next: () => {
        this.projectService.getEmotionReplies(this.projectId).subscribe({
          next: (dialogues) => {
            this.dialogues = dialogues;
            this.dialogues.forEach((dialogue) => {
              if (
                !`${dialogue.translatedDialogue ?? ""}`.trim() &&
                `${dialogue.dialogue ?? ""}`.trim()
              ) {
                dialogue.translatedDialogue = `${dialogue.dialogue}`.trim();
              }
            });
            this.savedDialogueIds = new Set(
              (dialogues || [])
                .map((dialogue) => dialogue.id)
                .filter((id) => !!id)
            );
            this.spinner = false;
          },
          error: () => {
            this.loadFailed = true;
            this.spinner = false;
          },
        });
      },
      error: () => {
        this.loadFailed = true;
        this.spinner = false;
      },
    });
  }
}
